import React, { useEffect, useState } from "react";
import {
  ScanLine, ArrowRight, ArrowLeft, Loader2, Search, Check, X, ShieldCheck,
  FileText, LayoutGrid, Focus, CreditCard, MessageSquareWarning,
  Image as ImageIcon,
} from "lucide-react";
import { toast } from "sonner";
import { api, describeError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { hasSeenExperience, dismissExperience } from "@/lib/expMode";
import { hasBootstrapped } from "@/lib/tenantBootstrap";
import { useClient } from "@/lib/clientContext";
import { TID } from "@/lib/testIds";
import EobEraFlow from "./EobEraFlow";
import NoteClaimFlow from "./NoteClaimFlow";
import MultiClaimFlow from "./claim/MultiClaimFlow";
import FileDropzone from "./FileDropzone";
import {
  Select, SelectGroup, SelectValue, SelectTrigger, SelectContent, SelectLabel, SelectItem,
} from "@/components/ui/select";

// The three-step pitch — covers both note→claim and EOB→ERA paths.
const STEPS = [
  { n: 1, t: "Process one document", s: "Scan a superbill, note, or paper EOB with Lens" },
  { n: 2, t: "See the intelligence", s: "Coded claim or generated 835 ERA, with sourced evidence" },
  { n: 3, t: "Decide", s: "Finish setup when you're ready to submit & get paid" },
];

// Supported file types (matches the accept="" on the hidden input).
const FORMATS = {
  pdf: { label: "PDF", icon: FileText },
  photo: { label: "Photo · JPG or PNG", icon: ImageIcon },
};

// Upload modes — copy from Samuel's "Scan with Lens" design.
// "single" (document → claim) and "era" (EOB → ERA) are wired to real endpoints.
const SCAN_MODES = [
  {
    key: "era",
    icon: CreditCard,
    live: true,
    title: "EOB → ERA",
    desc: "A paper EOB becomes an ERA — auto-posted to the right claims.",
    drop: "Lens reads your EOB and generates a downloadable 835 ERA.",
    formats: ["pdf"],
    detail: {
      tagline: "Turn a paper EOB into a downloadable X12 835 ERA — no setup required.",
      steps: [
        { t: "Read the remittance", s: "Lens extracts the payer, check number, paid amount, patient and date of service straight from the PDF." },
        { t: "Match & adjudicate", s: "Every remittance line is matched to your open claims, with billed / allowed / paid and CARC-RARC reason codes interpreted." },
        { t: "Generate the 835", s: "A standards-compliant X12 835 ERA is assembled and validated — ready to download and post." },
      ],
      notes: [
        "Runs the real revQ pipeline — it never submits or posts anything.",
        "One EOB PDF at a time · up to 50 MB in experience mode",
        "If the 835 can't balance yet, you'll see the exact blockers (e.g. missing payer / client ID mappings).",
      ],
    },
  },
  {
    key: "single",
    icon: FileText,
    live: true,
    title: "Single document → one claim",
    desc: "One note or superbill becomes one claim.",
    drop: "Lens reads it, codes it, and drafts one claim — in about 30 seconds.",
    formats: ["pdf", "photo"],
    detail: {
      tagline: "Turn one clinical note or superbill into a scrubbed, ready-to-bill claim — no setup required.",
      steps: [
        { t: "Read the document", s: "Lens reads the encounter note or superbill and pulls the visit, provider, patient and payer." },
        { t: "Code & link", s: "Every procedure (CPT) is coded and linked to the diagnosis (ICD-10) that justifies it, tracing back to the words in your note." },
        { t: "Scrub the claim", s: "The draft 837P is screened against payer rules — you see pass / fail and every finding before anything is submitted." },
      ],
      notes: [
        "Runs the real revQ pipeline — it never submits or posts anything.",
        "Uses the saved provider NPI you chose to begin.",
        "One document at a time · PDF or photo in experience mode.",
      ],
    },
  },
  {
    key: "batch",
    icon: LayoutGrid,
    live: true,
    title: "Batch → many claims",
    desc: "Many documents — each becomes its own claim.",
    drop: "Drop up to 10 documents. Lens reads every one and drafts a separate claim for each.",
    formats: ["pdf", "photo"],
    detail: {
      tagline: "Drop a batch of notes or superbills (up to 10) — revQ codes each one and returns a claim per document in a single job.",
      steps: [
        { t: "Read every document", s: "Lens reads each document, keeping medical notes for coding and reading insurance cards for payer detail." },
        { t: "Code each encounter", s: "Every document is coded independently — its own CPTs linked to the diagnoses that justify them." },
        { t: "Scrub the batch", s: "The whole batch is screened against payer rules — see each claim and the shared pass / fail findings." },
      ],
      notes: [
        "Runs the real revQ pipeline — it never submits or posts anything.",
        "Up to 10 documents per job · PDF or photo in experience mode.",
        "Uses the saved provider NPI you chose to begin.",
      ],
    },
  },
  {
    key: "visit",
    icon: Focus,
    title: "Multiple documents for one visit",
    desc: "Group several documents that all belong to a single visit — together they become one claim.",
    drop: "Lens combines the documents from a single visit into one claim.",
    formats: ["pdf", "photo"],
  },
  {
    key: "shawl",
    icon: MessageSquareWarning,
    title: "Denied EOB → Ask revQ (SHAWL)",
    desc: "A denied remittance — revQ reads why it was denied and opens a chat with the fixes that get it paid.",
    drop: "Lens reads the denial and opens a chat with the fixes to get it paid.",
    formats: ["pdf", "photo"],
  },
];

function isValidNpi(v) {
  return /^\d{10}$/.test((v || "").replace(/\D/g, ""));
}

// Single source of truth for the "Experience Mode" label — reused on every screen
// so size and style stay identical. `tone` only adjusts colors for dark vs light headers.
function ExperienceBadge({ tone = "light", className = "" }) {
  const dark = tone === "dark";
  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-[10px] font-extrabold tracking-[.1em] uppercase ${
        dark ? "bg-white/10 text-white border border-white/20" : "bg-[var(--ink)] text-white"
      } ${className}`}
    >
      <ScanLine size={11} /> Experience Mode
    </div>
  );
}

export default function ExperienceMode({ embedded = false, open: openProp = false, onClose, hideEra = false } = {}) {
  const { user } = useAuth();
  const { clients: tenantClients, loading: clientsLoading } = useClient();
  const [openState, setOpenState] = useState(false);
  const open = embedded ? openProp : openState;
  const setOpen = embedded ? () => {} : setOpenState;
  const [phase, setPhase] = useState("intro"); // intro → lens
  const [npi, setNpi] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [provider, setProvider] = useState(null);
  const [savedProviders, setSavedProviders] = useState([]);
  const [providersLoading, setProvidersLoading] = useState(true);
  const [firstClientId, setFirstClientId] = useState(null);
  const [clients, setClients] = useState([]);
  const clientFor = (p) => (p?.client_id ? clients.find((c) => c.client_id === p.client_id) || { client_id: p.client_id } : null);
  const primaryTaxonomy = (p) => {
    const tx = Array.isArray(p?.taxonomies) ? p.taxonomies : [];
    if (!tx.length) return null;
    const t = tx.find((x) => x.primary) || tx[0];
    return { code: t.code || t.taxonomy_code, desc: t.desc || t.description };
  };
  const groupedProviders = () => {
    const groups = {};
    for (const p of savedProviders) {
      const desc = primaryTaxonomy(p)?.desc || "Other";
      (groups[desc] ||= []).push(p);
    }
    return Object.entries(groups)
      .sort(([a], [b]) => (a === "Other" ? 1 : b === "Other" ? -1 : a.localeCompare(b)))
      .map(([desc, providers]) => ({
        desc,
        providers: [...providers].sort((x, y) =>
          (x.provider_name || "").localeCompare(y.provider_name || "")
        ),
      }));
  };
  const [mode, setMode] = useState(hideEra ? "single" : "era");
  const [lensStep, setLensStep] = useState("choose"); // choose → detail
  const [eraViewOpen, setEraViewOpen] = useState(false);
  const [noteResultOpen, setNoteResultOpen] = useState(false);
  const [eraFile, setEraFile] = useState(null);
  const [claimFile, setClaimFile] = useState(null);
  const [batchFiles, setBatchFiles] = useState(null);
  const modesList = hideEra ? SCAN_MODES.filter((m) => m.key !== "era") : SCAN_MODES;
  const activeMode = SCAN_MODES.find((m) => m.key === mode) || SCAN_MODES[0];

  // Context-aware back button — rendered in the header next to the EXPERIENCE MODE pill.
  const headerBack = phase === "intro"
    ? null
    : eraViewOpen
    ? { label: "Try another document", fn: () => { setEraViewOpen(false); setEraFile(null); setLensStep("choose"); } }
    : (eraFile || claimFile || batchFiles)
    ? { label: "Back", fn: () => { setEraFile(null); setClaimFile(null); setBatchFiles(null); } }
    : lensStep === "detail"
    ? { label: "Back", fn: () => setLensStep("choose") }
    : { label: "Change NPI", fn: () => { setLensStep("choose"); setPhase("intro"); } };

  const runMode = (payload) => {
    if (mode === "era") {
      if (!payload) return;
      setEraFile(payload);
    } else if (mode === "single") {
      if (!payload) return;
      setClaimFile(payload);
    } else if (mode === "batch") {
      const list = Array.isArray(payload) ? payload : payload ? [payload] : [];
      if (!list.length) return;
      setBatchFiles(list);
    } else {
      toast.message(`${activeMode.title} — coming soon`, {
        description: "This demo isn't wired up yet. EOB → ERA, Single document and Batch are live now.",
      });
    }
  };

  // Eligibility = the tenant has no NPIs (providers) loaded yet, and not previously dismissed.
  // Once at least one NPI is saved, Experience Mode no longer auto-launches on login.
  useEffect(() => {
    if (embedded || !user) return;
    // Dev-only override: /dashboard?expmode=1 force-opens it (ignored in production builds).
    const forced =
      process.env.NODE_ENV !== "production" &&
      new URLSearchParams(window.location.search).get("expmode") === "1";
    if (forced) { setOpen(true); return; }
    if (hasSeenExperience(user)) return;
    if (clientsLoading) return; // wait until we know the tenant's client count
    // Brand-new / empty tenant → the NPI bootstrap modal owns the first-run moment.
    // Once it provisions a client (or the user skips), clients updates and this re-runs to open.
    if (tenantClients.length === 0 && !hasBootstrapped(user)) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/api/edi/note-to-claim/providers");
        const npiCount = (data?.providers || []).length;
        if (!cancelled && npiCount === 0) setOpen(true);
      } catch {
        // Can't confirm eligibility → stay hidden rather than nag.
      }
    })();
    return () => { cancelled = true; };
  }, [user, clientsLoading, tenantClients.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Allow other screens (e.g. the Payments & ERA page "Upload EOB" button) to open this.
  useEffect(() => {
    if (embedded) return;
    const openit = () => setOpen(true);
    const closeit = () => setOpen(false);
    window.addEventListener("revq:open-experience", openit);
    window.addEventListener("revq:close-experience", closeit);
    return () => { window.removeEventListener("revq:open-experience", openit); window.removeEventListener("revq:close-experience", closeit); };
  }, [embedded]);

  // Broadcast open/closed so the side-menu "Experience mode" toggle can stay in sync.
  useEffect(() => {
    if (embedded) return;
    window.dispatchEvent(new CustomEvent("revq:experience-state", { detail: { open } }));
  }, [open, embedded]);

  // Embedded launcher (e.g. /create-claims PDF button): reset to a clean NPI → mode flow each open.
  useEffect(() => {
    if (!embedded || !openProp) return;
    setPhase("intro"); setProvider(null); setNpi(""); setError("");
    setEraFile(null); setClaimFile(null); setBatchFiles(null);
    setLensStep("choose"); setMode(hideEra ? "single" : "era"); setEraViewOpen(false); setNoteResultOpen(false);
  }, [embedded, openProp]);

  // Load the tenant's saved providers (for NPI picker) and first client id (for EOB auto-link).
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setProvidersLoading(true);
    (async () => {
      try {
        const [pRes, cRes] = await Promise.all([
          api.get("/api/edi/note-to-claim/providers"),
          api.get("/api/edi/note-to-claim/clients").catch(() => ({ data: {} })),
        ]);
        if (!cancelled) {
          setSavedProviders(pRes.data?.providers || []);
          const cl = cRes.data?.clients || [];
          setClients(cl);
          setFirstClientId(cl[0]?.client_id || null);
        }
      } catch { /* non-fatal — user can still type an NPI manually */ }
      finally { if (!cancelled) setProvidersLoading(false); }
    })();
    return () => { cancelled = true; };
  }, [open]);

  if (!open) return null;

  const close = () => {
    if (embedded) { onClose?.(); return; }
    dismissExperience(user);
    setOpen(false);
  };

  const onValidate = async (e) => {
    e?.preventDefault?.();
    setError("");
    const clean = npi.replace(/\D/g, "");
    if (!isValidNpi(clean)) {
      setError("Enter a valid 10-digit NPI.");
      return;
    }
    setBusy(true);
    try {
      // Per API.md: POST /providers/lookup, body { npi_number } → NPPES details (preview only).
      const { data } = await api.post("/api/edi/note-to-claim/providers/lookup", {
        npi_number: clean,
      });
      if (!data?.provider_name) {
        setError("We couldn't confirm that NPI. Double-check the number and try again.");
        return;
      }
      // Close the loop: persist the NPI under the tenant so it shows in the saved list.
      // Idempotent — re-sending an existing NPI returns the stored record (already_exists=true).
      let saved = data;
      try {
        const { data: saveRes } = await api.post("/api/edi/note-to-claim/providers", {
          npi_number: clean,
        });
        if (saveRes?.provider) {
          saved = saveRes.provider;
          setSavedProviders((prev) =>
            prev.some((p) => p.npi_number === saved.npi_number) ? prev : [...prev, saved]
          );
          toast.success(
            saveRes.already_exists ? "Already in your providers" : "Saved to your providers",
            { description: `${saved.provider_name} · NPI ${saved.npi_number}` }
          );
        }
      } catch {
        // Non-fatal — proceed with the NPPES preview so the demo isn't blocked.
      }
      setProvider(saved);
      setPhase("lens");
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusy(false);
    }
  };

  // User picked an already-saved provider — no NPPES lookup needed, it's ready to use.
  const selectSavedProvider = (p) => {
    if (!p) return;
    setError("");
    setProvider(p);
    setPhase("lens");
  };

  return (
    <div
      data-testid={TID.experienceOverlay}
      className="fixed inset-0 z-[200] flex items-center justify-center p-4 fade-up"
      style={{ background: "rgba(12,12,14,.55)", backdropFilter: "saturate(120%) blur(6px)" }}
    >
      <div
        className="w-full max-h-[92vh] overflow-y-auto rounded-[24px] bg-white/95 border border-white/60"
        style={{ width: noteResultOpen ? "min(1440px, 97vw)" : eraViewOpen ? "min(1200px, 96vw)" : "min(1040px, 68vw)", minWidth: noteResultOpen ? "min(1040px, 97vw)" : eraViewOpen ? "min(900px, 96vw)" : "min(720px, 94vw)", maxWidth: "97vw", boxShadow: "0 40px 120px rgba(0,0,0,.45)" }}
      >
        {/* Header */}
        {phase === "intro" && embedded ? (
          <div className="relative px-6 pt-5 pb-4 flex items-center gap-3 border-b border-black/[.06]">
            <div className="flex-1 min-w-0 pr-8">
              <div className="text-[16px] font-bold tracking-[-0.3px] text-[var(--ink)]">Upload a PDF</div>
              <div className="text-[11.5px] text-[var(--gray2)] mt-0.5">Pick a billing provider, then choose how you're uploading.</div>
            </div>
            <button
              onClick={close}
              data-testid={TID.experienceCloseBtn}
              aria-label="Close"
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/[.05] hover:bg-black/[.1] text-[var(--gray1)] flex items-center justify-center transition"
            >
              <X size={16} />
            </button>
          </div>
        ) : phase === "intro" ? (
          <div className="relative rounded-t-[24px] px-6 pt-6 pb-7 glass-dark text-white">
            <button
              onClick={close}
              data-testid={TID.experienceCloseBtn}
              aria-label="Close Experience Mode"
              className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center bg-white/10 hover:bg-white/20 text-white transition"
            >
              <X size={16} />
            </button>
            <ExperienceBadge tone="dark" />
            <div className="text-[21px] font-bold tracking-[-0.4px] mt-2">
              See RevQ work before you set anything up
            </div>
            <div className="text-[12px] text-white/60 mt-2 leading-relaxed max-w-[460px]">
              Drop in one real document — a superbill, note, or a paper EOB — and watch RevQ
              extract it, code it, score its readiness, and build the claim (or turn an EOB into a
              downloadable 835 ERA), showing you exactly where every value came from. The whole
              pipeline, no setup required. It runs for real but <b className="text-white/85">never
              submits</b>.
            </div>
          </div>
        ) : eraViewOpen || noteResultOpen ? null : (
          <div className="relative px-6 pt-5 pb-4 flex items-center gap-3 border-b border-black/[.06]">
            {headerBack ? (
              <button
                onClick={headerBack.fn}
                data-testid="experience-header-back-btn"
                className="inline-flex items-center gap-2 text-[13px] font-semibold text-[var(--ink)] bg-black/[.05] hover:bg-black/[.09] rounded-full pl-3 pr-4 py-2 transition flex-shrink-0"
              >
                <ArrowLeft size={17} /> {headerBack.label}
              </button>
            ) : null}
            <div className="flex-1 min-w-0 pr-8">
              {embedded ? null : <ExperienceBadge tone="light" />}
            </div>
            <button
              onClick={close}
              data-testid={TID.experienceCloseBtn}
              aria-label="Close Experience Mode"
              className="absolute top-4 right-4 w-8 h-8 rounded-full bg-black/[.05] hover:bg-black/[.1] text-[var(--gray1)] flex items-center justify-center transition"
            >
              <X size={16} />
            </button>
          </div>
        )}

        {phase === "intro" ? (
          <div className="px-6 py-6" data-testid="experience-intro">
            {/* Steps */}
            {embedded ? null : (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
              {STEPS.map((st) => (
                <div
                  key={st.n}
                  className="rounded-[12px] border border-black/[.08] bg-white/60 p-3"
                >
                  <div className="w-5 h-5 rounded-full bg-[var(--ink)] text-white text-[10px] font-extrabold flex items-center justify-center mb-2">
                    {st.n}
                  </div>
                  <div className="text-[11px] font-bold text-[var(--ink)] leading-tight">{st.t}</div>
                  <div className="text-[10px] text-[var(--gray2)] mt-1 leading-snug">{st.s}</div>
                </div>
              ))}
            </div>
            )}

            {/* NPI validation */}
            <form onSubmit={onValidate} className={embedded ? "" : "mt-6"}>
              {providersLoading ? (
                <div className="shimmer h-[11px] w-[140px] rounded-full" />
              ) : (
                <label className="text-[10px] font-bold tracking-[.05em] uppercase text-[var(--gray2)]">
                  Choose your NPI to begin
                </label>
              )}

              {providersLoading ? (
                <>
                  <div className="shimmer h-[10px] w-[230px] rounded-full mt-2 mb-2.5" />
                  <div
                    data-testid="experience-provider-loading"
                    className="shimmer h-[42px] w-full rounded-[11px] border border-black/[.08]"
                  />
                  <div className="flex items-center gap-3 my-3.5">
                    <div className="flex-1 h-px bg-black/[.08]" />
                    <span className="text-[9px] font-bold tracking-[.12em] uppercase text-[var(--gray3)]">or enter a new NPI</span>
                    <div className="flex-1 h-px bg-black/[.08]" />
                  </div>
                </>
              ) : savedProviders.length > 0 ? (
                <>
                  <div className="text-[11px] text-[var(--gray2)] mt-1 mb-2.5">
                    Pick one of your saved providers, or enter a new NPI below.
                  </div>
                  <div className="relative">
                    <Select
                      onValueChange={(val) => {
                        const p = savedProviders.find((sp) => sp.npi_number === val);
                        if (p) selectSavedProvider(p);
                      }}
                    >
                      <SelectTrigger
                        data-testid={TID.experienceProviderSelect}
                        className="w-full rounded-[11px] border border-black/[.12] bg-white px-3.5 py-2.5 h-auto text-[13px] focus:border-[var(--ink)] transition"
                      >
                        <SelectValue placeholder="Select a saved provider…" />
                      </SelectTrigger>
                      <SelectContent
                        position="popper"
                        side="top"
                        align="start"
                        sideOffset={6}
                        className="z-[300] w-[--radix-select-trigger-width]"
                      >
                        {groupedProviders().map((g) => (
                          <SelectGroup key={g.desc}>
                            <SelectLabel className="text-[10px] font-bold tracking-[.05em] uppercase text-[var(--gray2)]">
                              {g.desc}
                            </SelectLabel>
                            {g.providers.map((p) => (
                              <SelectItem key={p.npi_number} value={p.npi_number} className="text-[13px]">
                                {p.provider_name} · NPI {p.npi_number}{p.client_id ? ` · Client ${p.client_id}` : ""}
                              </SelectItem>
                            ))}
                          </SelectGroup>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="flex items-center gap-3 my-3.5">
                    <div className="flex-1 h-px bg-black/[.08]" />
                    <span className="text-[9px] font-bold tracking-[.12em] uppercase text-[var(--gray3)]">or enter a new NPI</span>
                    <div className="flex-1 h-px bg-black/[.08]" />
                  </div>
                </>
              ) : (
                <div className="text-[11px] text-[var(--gray2)] mt-1 mb-2.5">
                  We'll verify it against the national NPPES registry — no data is submitted anywhere.
                </div>
              )}

              <div className="flex items-start gap-3">
                <div className="flex-1">
                  <input
                    className="w-full rounded-[11px] border border-black/[.12] bg-white px-3.5 py-2.5 text-[13px] outline-none focus:border-[var(--ink)] transition"
                    placeholder="10-digit NPI · e.g. 1033110119"
                    value={npi}
                    data-testid={TID.experienceNpiInput}
                    inputMode="numeric"
                    maxLength={10}
                    onChange={(e) => {
                      setNpi(e.target.value.replace(/\D/g, "").slice(0, 10));
                      if (error) setError("");
                    }}
                  />
                  {error ? (
                    <div
                      data-testid={TID.experienceNpiError}
                      className="text-[11px] text-[var(--amber,#c0392b)] mt-1.5 font-medium"
                    >
                      {error}
                    </div>
                  ) : null}
                </div>
                <button
                  type="submit"
                  disabled={busy}
                  data-testid={TID.experienceValidateBtn}
                  className="btn-ink inline-flex items-center gap-1.5 whitespace-nowrap"
                >
                  {busy ? <Loader2 size={13} className="animate-spin" /> : <Search size={14} />}
                  {busy ? "Verifying…" : "Validate NPI"}
                </button>
              </div>
            </form>

            <div className="mt-6 flex items-center justify-between">
              <button
                onClick={close}
                data-testid={TID.experienceSkipBtn}
                className="text-[12px] font-semibold text-[var(--gray1)] hover:text-[var(--ink)] transition"
              >
                {embedded ? "Cancel" : "Skip to full setup"}
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col" data-testid={TID.experienceLensSection}>
            {eraFile ? (
              <EobEraFlow
                file={eraFile}
                autoLink={{ clientId: firstClientId, payerId: "XXXX", payeeNpi: provider?.npi_number }}
                eraViewOpen={eraViewOpen}
                onEraViewChange={setEraViewOpen}
                onBack={() => setEraFile(null)}
                onChooseFlow={() => { setEraFile(null); setEraViewOpen(false); setLensStep("choose"); }}
                onFinish={close}
              />
            ) : claimFile ? (
              <NoteClaimFlow
                file={claimFile}
                npi={provider?.npi_number}
                onBack={() => setClaimFile(null)}
                onResultOpenChange={setNoteResultOpen}
                onChooseFlow={() => { setClaimFile(null); setNoteResultOpen(false); setLensStep("choose"); }}
                onFinish={close}
              />
            ) : batchFiles ? (
              <MultiClaimFlow
                files={batchFiles}
                npi={provider?.npi_number}
                onBack={() => setBatchFiles(null)}
                onFinish={close}
              />
            ) : lensStep === "choose" ? (
            <div className="px-6 pt-5 pb-5">
              {/* Confirmed provider */}
              {provider ? (
                <div
                  data-testid={TID.experienceNpiConfirmed}
                  className="relative overflow-hidden rounded-[16px] border border-[rgba(52,199,89,.35)] px-4 py-3.5 flex items-center gap-3.5 mb-4"
                  style={{ background: "linear-gradient(135deg, rgba(52,199,89,.16), rgba(52,199,89,.04))" }}
                >
                  <div className="w-11 h-11 rounded-full bg-[var(--green,#34c759)] text-white flex items-center justify-center flex-shrink-0"
                    style={{ boxShadow: "0 6px 16px rgba(52,199,89,.4)" }}>
                    <Check size={20} strokeWidth={3} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[9px] font-extrabold tracking-[.12em] uppercase text-[var(--green-deep,#248a3d)]">
                        NPI Confirmed
                      </span>
                      <span className="inline-flex items-center gap-1 text-[8.5px] font-bold text-[var(--green-deep,#248a3d)]/80">
                        <ShieldCheck size={10} /> Verified · NPPES registry
                      </span>
                    </div>
                    <div className="text-[15px] font-bold text-[var(--ink)] truncate mt-0.5 tracking-[-0.2px]">
                      {provider.provider_name}
                    </div>
                    <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
                      <span className="inline-flex items-center text-[10.5px] font-semibold text-[var(--ink2)] bg-white/60 border border-[rgba(52,199,89,.3)] rounded-full px-2.5 py-0.5 tabular-nums tracking-[.02em]">
                        NPI · {provider.npi_number}
                      </span>
                      {clientFor(provider) ? (
                        <span data-testid={TID.experienceProviderClient}
                          className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-[var(--ink2)] bg-white/60 border border-[rgba(52,199,89,.3)] rounded-full px-2.5 py-0.5 tracking-[.02em]">
                          Client · {clientFor(provider).client_id}
                          {clientFor(provider).description ? <span className="text-[var(--gray2)] font-medium">— {clientFor(provider).description}</span> : null}
                        </span>
                      ) : null}
                      {primaryTaxonomy(provider) ? (
                        <span data-testid={TID.experienceProviderTaxonomy}
                          className="inline-flex items-center gap-1 text-[10.5px] font-semibold text-[var(--ink2)] bg-white/60 border border-[rgba(52,199,89,.3)] rounded-full px-2.5 py-0.5 tracking-[.02em]">
                          Taxonomy · {primaryTaxonomy(provider).code}
                          {primaryTaxonomy(provider).desc ? <span className="text-[var(--gray2)] font-medium">— {primaryTaxonomy(provider).desc}</span> : null}
                        </span>
                      ) : null}
                    </div>
                  </div>
                </div>
              ) : null}

              <div className="text-[10px] font-bold tracking-[.1em] uppercase text-[var(--gray2)]">
                How are you uploading?
              </div>

              <div className="mt-3 flex flex-col gap-2.5">
                {modesList.map((m) => {
                  const Icon = m.icon;
                  const on = mode === m.key;
                  const disabled = !m.live;
                  return (
                    <button
                      key={m.key}
                      type="button"
                      disabled={disabled}
                      onClick={() => { if (!disabled) setMode(m.key); }}
                      data-testid={TID.experienceLensCard(m.key)}
                      className={`w-full text-left rounded-[14px] border p-3.5 flex items-center gap-3 transition ${
                        disabled
                          ? "border-black/[.06] bg-black/[.02] opacity-55 cursor-not-allowed"
                          : on
                            ? "border-[var(--ink)] bg-white shadow-[0_4px_18px_rgba(0,0,0,.08)]"
                            : "border-black/[.08] bg-white/50 hover:border-black/[.18]"
                      }`}
                    >
                      <div className={`w-10 h-10 rounded-[11px] flex items-center justify-center flex-shrink-0 ${
                        on && !disabled ? "bg-[var(--ink)] text-white" : "bg-black/[.05] text-[var(--ink2)]"
                      }`}>
                        <Icon size={17} strokeWidth={1.9} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-[13px] font-bold text-[var(--ink)] flex items-center gap-2 flex-wrap">
                          {m.title}
                          {m.live ? (
                            <span className="text-[8.5px] font-bold px-2 py-0.5 rounded-full bg-[rgba(52,199,89,.13)] text-[var(--green-deep,#248a3d)] border border-[rgba(52,199,89,.3)] inline-flex items-center gap-1">
                              <span className="w-[5px] h-[5px] rounded-full bg-[var(--green-deep,#248a3d)]" /> Live
                            </span>
                          ) : (
                            <span className="text-[8.5px] font-bold px-2 py-0.5 rounded-full bg-black/[.05] text-[var(--gray2)] border border-black/[.1]">
                              Coming soon
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-[var(--gray2)] mt-0.5 leading-snug">{m.desc}</div>
                      </div>
                      <span className={`w-[18px] h-[18px] rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
                        on && !disabled ? "border-[var(--ink)]" : "border-black/20"
                      }`}>
                        {on && !disabled ? <span className="w-[9px] h-[9px] rounded-full bg-[var(--ink)]" /> : null}
                      </span>
                    </button>
                  );
                })}
              </div>

              <button
                type="button"
                onClick={() => { if (activeMode.live) setLensStep("detail"); else runMode(null); }}
                disabled={!activeMode.live}
                data-testid="experience-continue-btn"
                className="btn-ink w-full justify-center mt-4 inline-flex items-center gap-1.5 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Continue with {activeMode.title} <ArrowRight size={14} />
              </button>
            </div>
            ) : (
            /* Detail screen for the selected (live) flow */
            <div className="px-6 pt-5 pb-5" data-testid="experience-flow-detail">
              {/* Header */}
              <div className="flex items-center gap-3.5">
                <div className="w-12 h-12 rounded-[14px] bg-[var(--ink)] text-white flex items-center justify-center flex-shrink-0">
                  <activeMode.icon size={22} strokeWidth={1.9} />
                </div>
                <div className="min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="text-[19px] font-bold text-[var(--ink)] tracking-[-0.3px]">{activeMode.title}</div>
                    <span className="text-[8.5px] font-bold px-2 py-0.5 rounded-full bg-[rgba(52,199,89,.13)] text-[var(--green-deep,#248a3d)] border border-[rgba(52,199,89,.3)] inline-flex items-center gap-1">
                      <span className="w-[5px] h-[5px] rounded-full bg-[var(--green-deep,#248a3d)]" /> Live
                    </span>
                  </div>
                  <div className="text-[12px] text-[var(--gray2)] mt-1 leading-snug max-w-[540px]">
                    {activeMode.detail?.tagline || activeMode.desc}
                  </div>
                </div>
              </div>

              {/* Two columns: how-it-works + dropzone */}
              <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4 items-stretch">
                {/* How it works */}
                <div className="flex flex-col">
                  <div className="text-[10px] font-bold tracking-[.1em] uppercase text-[var(--gray2)] mb-2.5">
                    How it works
                  </div>
                  <div className="flex flex-col gap-2.5">
                    {(activeMode.detail?.steps || []).map((st, i) => (
                      <div key={i} className="flex gap-3 rounded-[12px] border border-black/[.07] bg-white/60 p-3">
                        <div className="w-6 h-6 rounded-full bg-[var(--ink)] text-white text-[10px] font-extrabold flex items-center justify-center flex-shrink-0">
                          {i + 1}
                        </div>
                        <div className="min-w-0">
                          <div className="text-[12px] font-bold text-[var(--ink)] leading-tight">{st.t}</div>
                          <div className="text-[10.5px] text-[var(--gray2)] mt-0.5 leading-snug">{st.s}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  {(activeMode.detail?.notes || []).length ? (
                    <ul className="mt-3 space-y-1.5">
                      {activeMode.detail.notes.map((n, i) => (
                        <li key={i} className="flex gap-2 text-[10.5px] text-[var(--gray2)] leading-snug">
                          <Check size={12} className="text-[var(--green-deep,#248a3d)] mt-[2px] flex-shrink-0" />
                          <span>{n}</span>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>

                {/* Dropzone */}
                <div className="flex flex-col">
                  <div className="text-[10px] font-bold tracking-[.1em] uppercase text-[var(--gray2)] mb-2.5">
                    Drop your file
                  </div>
                  <FileDropzone
                    testId="experience-lens-dropzone"
                    inputTestId="experience-lens-file-input"
                    browseTestId="experience-lens-browse"
                    disabled={!activeMode.live}
                    accept=".pdf,image/png,image/jpeg"
                    hint={activeMode.drop}
                    formats={activeMode.formats.map((k) => FORMATS[k])}
                    multiple={mode === "batch"}
                    maxFiles={10}
                    onFile={(f) => runMode(f)}
                    onFiles={(fs) => runMode(fs)}
                  />
                </div>
              </div>
            </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
